// React core and hooks for state management
import React, { useState, useEffect, useCallback } from 'react';

// Bootstrap UI components for layout, modals, and notifications
import { Toast, ToastContainer, Button, Modal, Dropdown } from 'react-bootstrap';

// Font Awesome for icon support in buttons and UI
import 'font-awesome/css/font-awesome.min.css';

// Libraries for exporting table data
import jszip from 'jszip';
import pdfmake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// DataTables and plugins for advanced table features
import DataTable from 'datatables.net-react';
import DataTablesCore from 'datatables.net-bs5';
import 'datatables.net-buttons-bs5';
import 'datatables.net-buttons/js/buttons.colVis.mjs';
import 'datatables.net-buttons/js/buttons.html5.mjs';
import 'datatables.net-buttons/js/buttons.print.mjs';
import 'datatables.net-responsive-bs5';

// Cookie library for session and user data management
import Cookies from 'js-cookie';

// Register DataTables plugins
DataTablesCore.Buttons.jszip(jszip);
DataTablesCore.Buttons.pdfMake(pdfmake);
DataTable.use(DataTablesCore);

const OrgRoles = () => {
  // State for notifications
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [notificationHeader, setNotificationHeader] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationType, setNotificationType] = useState('success');

  // State for active tab and modals
  const [activeTab, setActiveTab] = useState('users');
  const [selectedRole, setSelectedRole] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState('');
  const [editingUserRoleID, setEditingUserRoleID] = useState('');

  // State for live data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]); // For dropdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // State for organization selector (for root users)
  const [organization, setOrganization] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedOrgID, setSelectedOrgID] = useState("");
  const [showOrgSelector, setShowOrgSelector] = useState(true);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  useEffect(() => {
    if (showUserModal && selectedUser && availableRoles.length > 0) {
      // Try to find the user's current role in the dropdown
      const currentRole = availableRoles.find(role => role.value === selectedUser.role || role.name === selectedUser.role);

      if (currentRole) {
        setEditingUserRole(currentRole.name);
        setEditingUserRoleID(currentRole.id.toString());
      } else {
        // If the user's role isn't in the dropdown, default to the first available role
        setEditingUserRole(availableRoles[0].name);
        setEditingUserRoleID(availableRoles[0].id.toString());
      }
    }
  }, [showUserModal, selectedUser, availableRoles]);

  // Check OrgID cookie and manage organization selector visibility
  useEffect(() => {
    let cookieOrgID = Cookies.get('OrgID');

    // 1. Decode and parse the cookie value
    if (cookieOrgID) {
      cookieOrgID = decodeURIComponent(cookieOrgID);
    }

    if (cookieOrgID && cookieOrgID.startsWith('j:')) {
      try {
        const orgArr = JSON.parse(cookieOrgID.slice(2));
        if (Array.isArray(orgArr) && orgArr.length > 0 && orgArr[0].orgID) {
          cookieOrgID = orgArr[0].orgID;
        } else {
          cookieOrgID = null;
        }
      } catch (err) {
        console.error("Error parsing OrgID cookie:", err);
        cookieOrgID = null;
      }
    }

    // 2. If cookie exists (org admin), hide selector and set org ID
    if (cookieOrgID) {
      setSelectedOrgID(cookieOrgID);
      setShowOrgSelector(false);
      if (organization && organization.length > 0) {
        const matchedOrg = organization.find(org => org.orgID === cookieOrgID);
        if (matchedOrg) {
          setSelectedOrg(matchedOrg.name);
        }
      }
    } else {
      // 3. If no cookie (super admin), show selector
      setShowOrgSelector(true);
    }
  }, [organization]);

  // Fetch organizations for selector (root users)
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const url = process.env.REACT_APP_BACKURL;
        if (!url) {
          throw new Error("REACT_APP_BACKURL is not defined");
        }
        const orgResponse = await fetch(url + '/api/foodPantry/organization', {
          credentials: 'include',
        });
        if (!orgResponse.ok) {
          throw new Error(`Organization error! Status: ${orgResponse.status}`);
        }
        const orgData = await orgResponse.json();
        if (orgData?.OrgNames?.length) {
          setOrganization(orgData.OrgNames);
          // Always set first organization as default
          const firstOrg = orgData.OrgNames[0];
          setSelectedOrg(firstOrg.name);
          setSelectedOrgID(firstOrg.orgID);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
  }, []);

  // Refetch users and roles when organization selection changes
  useEffect(() => {
    if (selectedOrgID) {
      fetchRolesAndUsers();
      fetchAvailableRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgID]);

  const handleSelectedOrgID = (orgName) => {
    const matchedOrg = organization.find(org => org.name === orgName);
    if (matchedOrg) {
      setSelectedOrgID(matchedOrg.orgID);
    } else {
      setSelectedOrgID(null);
      console.error("Organization ID not found for the selected organization.");
    }
  };

  const handleOrgChange = (e) => {
    const selectedName = e.target.value;
    setSelectedOrg(selectedName);
    handleSelectedOrgID(selectedName);
  };

  const fetchRolesAndUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let url = process.env.REACT_APP_BACKURL + '/api/foodPantry/org/users-and-roles'
      
      // For root users, append selected orgID to query string
      if (selectedOrgID) {
        url += `?orgID=${selectedOrgID}`;
      }

      // Fetch users and roles data from your backend
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
        credentials: 'include', // This will include cookies (sessionID, userID)
        cache: 'no-store' // Explicitly prevent caching
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.users || !Array.isArray(data.users)) {
        throw new Error('Invalid data format received from server');
      }

      // Process the users data from your backend format
      const processedUsers = data.users.map(user => ({
        id: user.userID,
        userID: user.userID,
        name: formatDisplayName(user.username, user.email),
        username: user.username,
        email: user.email,
        role: user.roleName || 'No Role Assigned', // roles comes as comma-separated string
        roleID: user.roleID || 'No Role Assigned',
        organization: extractOrganization(user.username, user.email),
      }));

      // Create role statistics from users data
      const roleStats = {};
      processedUsers.forEach(user => {
        const userRoles = user.role.split(', ').filter(role => role && role !== 'No Role Assigned');
        userRoles.forEach(role => {
          roleStats[role] = (roleStats[role] || 0) + 1;
        });
      });

      // Define role information with actual counts
      const processedRoles = [
        {
          id: 1,
          name: 'Super Admin',
          description: 'Full system access with all permissions',
          userCount: roleStats['superAdmin'] || 0,
          permissions: [
            'manage_users', 'manage_roles', 'manage_organizations',
            'manage_clients', 'manage_volunteers', 'view_reports',
            'manage_food_pantry', 'system_settings'
          ]
        },
        {
          id: 2,
          name: 'Organization Admin',
          description: 'Administrative access within assigned organizations',
          userCount: roleStats['orgAdmin'] || 0,
          permissions: [
            'manage_volunteers', 'manage_clients', 'view_reports',
            'manage_food_pantry', 'organization_settings'
          ]
        },
        {
          id: 3,
          name: 'Volunteer Coordinator',
          description: 'Manage volunteers and coordinate activities',
          userCount: roleStats['volunteerCoordinator'] || 0,
          permissions: [
            'manage_volunteers', 'view_clients', 'view_reports', 'manage_food_pantry'
          ]
        },
        {
          id: 4,
          name: 'Case Worker',
          description: 'Work with clients and manage client services',
          userCount: roleStats['caseWorker'] || 0,
          permissions: [
            'manage_clients', 'view_reports', 'manage_food_pantry'
          ]
        },
        {
          id: 5,
          name: 'Volunteer',
          description: 'Basic volunteer access for day-to-day operations',
          userCount: roleStats['volunteer'] || 0,
          permissions: [
            'view_clients', 'manage_food_pantry'
          ]
        },
        {
          id: 6,
          name: 'Client',
          description: 'Client access to personal services and information',
          userCount: roleStats['client'] || 0,
          permissions: [
            'view_personal_info', 'request_services'
          ]
        }
      ];

      setUsers(processedUsers);
      setRoles(processedRoles);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      showNotificationBanner_func('Error', `Failed to load data: ${err.message}`, 'danger');
    } finally {
      setLoading(false);
    }
  }, [selectedOrgID]);

  const fetchAvailableRoles = useCallback(async () => {
    try {
      const url = process.env.REACT_APP_BACKURL + '/api/foodPantry/org/all-roles'

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
        credentials: 'include',
        cache: 'no-store' // Explicitly prevent caching
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch roles: ${response.status}`);
      }

      const data = await response.json();

      if (data.roles && Array.isArray(data.roles)) {
        // Format the roles for the dropdown
        const formattedRoles = data.roles.map(role => ({
          id: role.roleID,
          name: formatRoleDisplay(role.roleName),
          value: role.roleName
        }));
        //console.log('Formatted roles:', formattedRoles);
        setAvailableRoles(formattedRoles);
      }
    } catch (err) {
      console.error('Error fetching available roles:', err);
      showNotificationBanner_func('Warning', 'Failed to load available roles for dropdown', 'warning');
    }
  }, []);

  // Helper function to format display name from username and email
  const formatDisplayName = (username, email) => {
    // Remove [test] prefix if present
    const cleanUsername = username.replace(/^\[test\]/, '');

    // If username is descriptive (like names), format it nicely
    if (cleanUsername.includes('admin') || cleanUsername === 'root') {
      return cleanUsername.charAt(0).toUpperCase() + cleanUsername.slice(1);
    }

    // Try to extract name from email or username
    const emailName = email.split('@')[0].replace(/^\[test\]/, '');
    if (emailName.includes('.')) {
      return emailName.split('.').map(part =>
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ');
    }

    // Fallback to formatted username
    return cleanUsername.charAt(0).toUpperCase() + cleanUsername.slice(1);
  };

  // Helper function to format role display
  const formatRoleDisplay = (role) => {
    const roleMap = {
      'orgAdmin': 'Organization Admin',
      'orgSupervisor': 'Organization Supervisor',
      'volunteer': 'Volunteer',
      'client': 'Client'
    };
    return roleMap[role] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Helper function to extract organization from user data
  const extractOrganization = (username, email) => {
    if (username.includes('admin') || username === 'root') {
      return 'System Administration';
    }

    const domain = email.split('@')[1];
    if (domain === 'example.com') {
      return 'General Organization';
    }

    // Extract organization name from domain
    const orgName = domain.split('.')[0];
    return orgName.charAt(0).toUpperCase() + orgName.slice(1);
  };

  // Handle user role update - now integrated with backend
  const handleUserRoleUpdate = async () => {
    if (selectedUser && editingUserRoleID) {
      try {
        //console.log('Updating user role:', selectedUser, editingUserRoleID);
        setUpdating(true);

        const url = process.env.REACT_APP_BACKURL + '/api/foodPantry/org/user-role'
        const currentUserID = Cookies.get('userID');

        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            targetUserID: selectedUser.userID,
            roleID: editingUserRoleID,
            orgID: selectedOrgID,
            currentUserID: currentUserID
          }),
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Server error: ${response.status}`);
        }

        const result = await response.json();

        // If orgID is returned, set it in the cookie for the promoted user
        if (result.orgID) {
          Cookies.set('OrgID', result.orgID);
        }

        // Update the local state to reflect the change
        const updatedUsers = users.map(user =>
          user.id === selectedUser.id
            ? { ...user, role: editingUserRole }
            : user
        );
        setUsers(updatedUsers);

        showNotificationBanner_func('Success', `Successfully updated ${selectedUser.name}'s role to ${editingUserRole}`, 'success');

        // Close modal and reset state
        setShowUserModal(false);
        setSelectedUser(null);
        setEditingUserRole('');
        setEditingUserRoleID('');

      } catch (err) {
        console.error('Error updating user role:', err);
        showNotificationBanner_func('Error', `Failed to update user role: ${err.message}`, 'danger');
      } finally {
        setUpdating(false);
      }
    }
  };

  // Handle opening user edit modal
  const handleUserClick = (user) => {
    setSelectedUser(user);

    // Find the current role in available roles
    const currentRole = availableRoles.find(role =>
      formatRoleDisplay(role.value) === user.role ||
      role.name === user.role
    );

    if (currentRole) {
      setEditingUserRole(currentRole.name);
      setEditingUserRoleID(currentRole.id.toString());
    } else {
      // Default to first role if current role not found
      if (availableRoles.length > 0) {
        setEditingUserRole(availableRoles[0].name);
        setEditingUserRoleID(availableRoles[0].id.toString());
      }
    }

    setShowUserModal(true);
  };

  // Handle role selection change
  const handleRoleSelectionChange = (roleId) => {
    const selectedRole = availableRoles.find(role => role.id.toString() === roleId);
    if (selectedRole) {
      setEditingUserRole(selectedRole.name);
      setEditingUserRoleID(roleId);
    }
  };

  // Show notification banner
  function showNotificationBanner_func(header, body, type) {
    setNotificationHeader(header);
    setNotificationBody(body);
    setNotificationType(type);
    setShowNotificationBanner(true);
    setTimeout(() => {
      setShowNotificationBanner(false);
    }, 5000);
  }

  const getRoleColor = (roleName) => {
    const colors = {
      'Super Admin': 'danger',
      'Organization Admin': 'primary',
      'Organization Supervisor': 'primary',
      'Volunteer Coordinator': 'info',
      'Case Worker': 'warning',
      'Volunteer': 'success',
      'Client': 'secondary'
    };
    return colors[roleName] || 'secondary';
  };

  // Get contrast text color for notifications
  const getContrastTextColor = (type) => {
    const lightTypes = ['warning', 'light', 'info'];
    return lightTypes.includes(type?.toLowerCase()) ? '#000' : '#fff';
  };

  // DataTable columns for users
  const columnsUsers = [
    {
      title: 'Name',
      data: 'name',
      render: function (data, type, row) {
        const initials = data.split(' ').map(n => n[0]).join('');
        return `
          <div class="d-flex align-items-center">
            <div class="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" style="width: 32px; height: 32px;">
              <span class="text-white fw-bold" style="font-size: 12px;">${initials}</span>
            </div>
            ${data}
          </div>
        `;
      }
    },
    { title: 'Username', data: 'username' },
    { title: 'Email', data: 'email' },
    {
      title: 'Role',
      data: 'role',
      render: function (data, type, row) {
        if (data === 'No Role Assigned') {
          return '<span class="badge bg-warning">No Role Assigned</span>';
        }

        // Handle multiple roles (comma-separated)
        const roles = data.split(', ');
        return roles.map(role => {
          const formattedRole = formatRoleDisplay(role);
          const color = getRoleColor(formattedRole);
          return `<span class="badge bg-${color} me-1">${formattedRole}</span>`;
        }).join('');
      }
    },

  ];

  if (loading) {
    return (
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-3">
        <div className="card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading roles and permissions data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-3">
        <div className="card-body text-center">
          <div className="alert alert-danger" role="alert">
            <i className="fa fa-exclamation-triangle me-2"></i>
            Error loading data: {error}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              fetchRolesAndUsers();
              fetchAvailableRoles();
            }}
          >
            <i className="fa fa-refresh me-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }



  return (
    <React.Fragment>
      {/* Toast notification banner */}
      <ToastContainer className="p-5" position="top-end" style={{ zIndex: 9999 }}>
        <Toast
          show={showNotificationBanner}
          onClose={() => setShowNotificationBanner(false)}
          animation={true}
          className={`toast-banner rounded-4 toast-banner-${notificationType.toLowerCase()}`}
        >
          <Toast.Header closeButton>
            <img
              src={
                localStorage.getItem("BurchCoin") === JSON.stringify(true)
                  ? './images/Burchcoin.png'
                  : './images/BridgingHopeNoWord.svg'
              }
              className="rounded me-2"
              alt="Notification Icon"
              style={{ width: '30px', height: '30px' }}
            />
            <strong className="me-auto">{notificationHeader}</strong>
          </Toast.Header>
          <Toast.Body>
            {notificationBody}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Main card container */}
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5 card-body container my-3">
        <div className="card-header d-flex justify-content-between align-items-center pb-3">
          <h3>
            <span>
              <i className="bi bi-shield-check pe-3"></i>
              Organization Permission Management
            </span>
          </h3>

          <div className="d-flex align-items-center">
            <button
              className="btn btn-outline-secondary btn-sm me-3"
              onClick={() => {
                fetchRolesAndUsers();
                fetchAvailableRoles();
              }}
              disabled={loading}
              title="Refresh Data"
            >
              <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'} me-1`}></i>
              Refresh
            </button>
            <div className="btn-group" role="group">
              <button
                className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fa fa-users me-2"></i>Users ({users.length})
              </button>
            </div>
          </div>
        </div>

        <div className="card-body text-dark" id="roles-permissions-table-container">
          {/* Organization Selector for Root Users */}
          {showOrgSelector && (
            <div className="mb-4 p-3 bg-light border rounded">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <label htmlFor="orgSelect" className="form-label fw-bold mb-2">
                    Select Organization
                  </label>
                  {loadingOrgs ? (
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <select
                      id="orgSelect"
                      className="form-select"
                      value={selectedOrg}
                      onChange={handleOrgChange}
                    >
                      <option value="">-- Select an Organization --</option>
                      {organization.map((org) => (
                        <option key={org.orgID} value={org.name}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <div className="mb-3">
                <small className="text-muted">
                  <i className="fa fa-info-circle me-1"></i>
                  Click on any user row to modify their role
                </small>
              </div>
              <DataTable
                data={users}
                columns={columnsUsers}
                options={{
                  paging: true,
                  pageLength: 10,
                  scrollX: true,
                  searching: true,
                  ordering: true,
                  responsive: false,
                  dom: 'Bfrtip',
                  buttons: [
                    {
                      extend: 'collection',
                      text: 'Export',
                      buttons: [
                        {
                          extend: 'excel',
                          text: 'Export to Excel'
                        },
                        {
                          extend: 'pdf',
                          text: 'Export to PDF'
                        },
                        {
                          extend: 'print',
                          text: 'Print'
                        }
                      ]
                    }
                  ],
                  language: {
                    info: "_START_-_END_ of _TOTAL_ users",
                    infoEmpty: "No users available",
                    infoFiltered: "(filtered from _MAX_ total users)",
                  },
                  rowCallback: function (row, data, index) {
                    // Add click handler to each row
                    row.style.cursor = 'pointer';
                    row.onclick = function () {
                      handleUserClick(data);
                    };
                  },
                  initComplete: function () {
                    const buttonsContainer = document.querySelector('.dt-buttons');
                    if (buttonsContainer) {
                      buttonsContainer.classList.add('d-flex', 'gap-3');
                    }
                    const api = this.api && typeof this.api === 'function' ? this.api() : null;
                      if (!api) return;

                    // Safe adjust helper: wrap in RAF and try/catch
                    const safeAdjust = () => {
                      try {
                        if (typeof window.requestAnimationFrame === 'function') {
                          window.requestAnimationFrame(() => {
                            try { api.columns.adjust(); } catch (err) { /* swallow */ }
                          });
                        } else {
                          api.columns.adjust();
                        }
                      } catch (e) {
                        /* swallow */
                      }
                    };

                    // Debounce helper
                    let resizeTimer = null;
                    const scheduleAdjust = (delay = 100) => {
                      if (resizeTimer) clearTimeout(resizeTimer);
                      resizeTimer = setTimeout(() => {
                        safeAdjust();
                        resizeTimer = null;
                      }, delay);
                    };

                    // Use ResizeObserver on the table container, compare width to avoid needless adjusts
                    const container = document.getElementById('roles-permissions-table-container') || document.body;
                    let lastWidth = container ? Math.round(container.getBoundingClientRect().width) : null;

                    if (typeof ResizeObserver !== 'undefined') {
                      const ro = new ResizeObserver(entries => {
                        for (const entry of entries) {
                          const w = Math.round(entry.contentRect.width);
                          if (w !== lastWidth) {
                            lastWidth = w;
                            scheduleAdjust(120);
                          }
                        }
                      });
                      try { ro.observe(container); } catch (e) { /* ignore if observe fails */ }

                      // cleanup on destroy
                      api.on && api.on('destroy', () => {
                        try { ro.disconnect(); } catch (e) {}
                        if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null; }
                      });
                    } else {
                      // fallback: debounced window resize
                      const onWinResize = () => scheduleAdjust(150);
                      window.addEventListener('resize', onWinResize);
                      api.on && api.on('destroy', () => {
                        window.removeEventListener('resize', onWinResize);
                        if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null; }
                      });
                    }

                    // initial adjust after init
                    scheduleAdjust(50);
                  },
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Role Details Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Role Details: {selectedRole?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRole && (
            <div className="row">
              <div className="col-md-6">
                <h6>Basic Information</h6>
                <p><strong>Name:</strong> {selectedRole.name}</p>
                <p><strong>Description:</strong> {selectedRole.description}</p>
                <p><strong>Users with this role:</strong> {selectedRole.userCount}</p>
              </div>
              <div className="col-md-6">
                <h6>Permissions ({selectedRole.permissions.length})</h6>
                <div className="list-group list-group-flush">
                  {selectedRole.permissions.map(permission => (
                    <div key={permission} className="list-group-item d-flex justify-content-between align-items-center px-0">
                      <div>
                        <i className="fa fa-check-circle text-success me-2"></i>
                        {permission.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              showNotificationBanner_func('Info', `Edit role: ${selectedRole?.name}`, 'info');
              setShowRoleModal(false);
            }}
          >
            Edit Role
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User Edit Modal - Updated with backend integration */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Edit User: {selectedUser?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <div className="mb-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                    <span className="text-white fw-bold">
                      {selectedUser.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h6 className="mb-0">{selectedUser.username}</h6>
                    <small className="text-muted">{selectedUser.email}</small>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="userRole" className="form-label">
                  <strong>Role Assignment</strong>
                </label>
                <select
                  id="userRole"
                  className="form-select"
                  value={editingUserRoleID}
                  onChange={(e) => handleRoleSelectionChange(e.target.value)}
                  disabled={updating || availableRoles.length === 0}
                >
                  {availableRoles.length === 0 ? (
                    <option value="">Loading roles...</option>
                  ) : (
                    availableRoles
                      .filter(role => role.name !== 'Organization Admin')
                      .map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))
                  )}
                </select>
              </div>

              {editingUserRole !== selectedUser?.role && editingUserRole && (
                <div className="alert alert-info">
                  <i className="fa fa-info-circle me-2"></i>
                  Role will be changed from <strong>{formatRoleDisplay(selectedUser.role)}</strong> to <strong>{editingUserRole}</strong>
                  <br />
                </div>
              )}

            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowUserModal(false);
              setSelectedUser(null);
              setEditingUserRole('');
              setEditingUserRoleID('');
            }}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUserRoleUpdate}
            disabled={!editingUserRoleID || updating || availableRoles.length === 0}
          >
            {updating ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Updating...</span>
                </div>
                Updating...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2"></i>
                Update Role
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default OrgRoles;